// The EXIF allowlist (#352). A photo's camera, lens, date taken and shot settings stay; everything else in its EXIF goes: the GPS
// directory, the maker note, the second directory and its thumbnail, serial and unique ids, software, host computer, the other
// dates. The kept data is untrusted, so nothing is copied: every kept tag is read as a checked value and written again into a new
// little-endian block. A value that fails a check drops that tag, not the file. The Kotlin port (ExifAllow.kt) and the Swift port
// (ExifAllow.swift) give the same bytes; shared/cases/exif-allowlist.json (made by scripts/make-exif-cases.py) holds the cases.

import type { PhotoInfo } from "./protocol.ts";

const MAX_BLOCK = 1024;
const MAX_ENTRIES = 1000;
const TYPE_SIZE: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 };

interface Tag {
  type: number;
  count: number;
  raw: Uint8Array;
}
type Tags = Map<number, Tag>;

// A loop, not a spread: a spread of a long array throws a RangeError.
const ascii = (b: Uint8Array, at: number, n: number) => {
  let s = "";
  for (let i = at; i < Math.min(b.length, at + n); i++) s += String.fromCharCode(b[i]!);
  return s;
};

/** A place written out: a GPS field name or ISO 6709 coordinates (the same patterns as the read-back in mediaClean.ts). A kept string that holds one loses its tag. */
const PLACE = /GPSLatitude|GPSLongitude|GPSAltitude|GPSPosition|exif:GPS|ISO6709|[+-]\d{2,3}\.\d{3,}[+-]\d{3}\.\d{3,}|[+-]\d{4}\.\d{2,}[+-]\d{5}\.\d{2,}/;
const INT_MAX = 2 ** 31 - 1;

/** The tags of IFD0 and of the Exif IFD, or null when the block is not a TIFF block. */
function parse(input: Uint8Array): { le: boolean; ifd0: Tags; exif: Tags } | null {
  const block = input.length >= 6 && ascii(input, 0, 6) === "Exif\0\0" ? input.subarray(6) : input;
  const n = block.length;
  if (n < 8) return null;
  const order = ascii(block, 0, 2);
  if (order !== "II" && order !== "MM") return null;
  const le = order === "II";
  const dv = new DataView(block.buffer, block.byteOffset, n);
  if (dv.getUint16(2, le) !== 42) return null;
  const ifd = (off: number): Tags | null => {
    if (off < 8 || off + 2 > n) return null;
    const count = dv.getUint16(off, le);
    if (count > MAX_ENTRIES || off + 2 + 12 * count > n) return null;
    const found: Tags = new Map();
    for (let i = 0; i < count; i++) {
      const at = off + 2 + 12 * i;
      const tag = dv.getUint16(at, le);
      const type = dv.getUint16(at + 2, le);
      const cnt = dv.getUint32(at + 4, le);
      const unit = TYPE_SIZE[type];
      if (unit === undefined || found.has(tag)) continue;
      const size = unit * cnt;
      let start = at + 8;
      if (size > 4) {
        start = dv.getUint32(at + 8, le);
        if (start + size > n) continue;
      }
      found.set(tag, { type, count: cnt, raw: block.subarray(start, start + size) });
    }
    return found;
  };
  const root = dv.getUint32(4, le);
  const ifd0 = ifd(root);
  if (!ifd0) return null;
  let exif: Tags = new Map();
  const ptr = ifd0.get(0x8769);
  if (ptr && ptr.type === 4 && ptr.count === 1) {
    const off = new DataView(ptr.raw.buffer, ptr.raw.byteOffset, 4).getUint32(0, le);
    if (off !== root) exif = ifd(off) ?? new Map();
  }
  return { le, ifd0, exif };
}

// ---- Checks: each returns the value to write, or undefined to drop the tag ----

/** A string of printable ASCII: bytes after the first NUL are ignored, other bytes are removed, runs of spaces are one space. */
function text(t: Tag): string | undefined {
  if (t.type !== 2 || t.count < 1) return;
  const nul = t.raw.indexOf(0);
  const raw = nul < 0 ? t.raw : t.raw.subarray(0, nul);
  let s = "";
  for (const b of raw) if (b >= 0x20 && b <= 0x7e) s += String.fromCharCode(b);
  s = s.replace(/ +/g, " ").replace(/^ | $/g, "");
  return s.length >= 1 && s.length <= 64 && !PLACE.test(s) ? s : undefined;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/** [numerator, denominator], reduced, when the value is finite and within [lo, hi]. */
function ratAt(dv: DataView, at: number, le: boolean, signed: boolean, lo: number, hi: number, unknown = false): [number, number] | undefined {
  let n = signed ? dv.getInt32(at, le) : dv.getUint32(at, le);
  let d = signed ? dv.getInt32(at + 4, le) : dv.getUint32(at + 4, le);
  if (unknown && n === 0 && d === 0) return [0, 0]; // LensSpecification: 0/0 is "unknown"
  if (d === 0) return;
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const v = n / d;
  if (!Number.isFinite(v) || v < lo || v > hi) return;
  const g = gcd(Math.abs(n), d);
  n /= g;
  d /= g;
  if (signed && (Math.abs(n) > INT_MAX || d > INT_MAX)) return; // it would not fit the signed field it is written in
  return [n, d];
}

const view = (t: Tag) => new DataView(t.raw.buffer, t.raw.byteOffset, t.raw.length);

function rational(t: Tag | undefined, le: boolean, lo: number, hi: number, signed = false) {
  if (!t || t.type !== (signed ? 10 : 5) || t.count !== 1) return;
  return ratAt(view(t), 0, le, signed, lo, hi);
}

function whole(t: Tag | undefined, le: boolean, lo: number, hi: number, longs = false): number | undefined {
  if (!t || t.count !== 1 || (t.type !== 3 && !(longs && t.type === 4))) return;
  const v = t.type === 3 ? view(t).getUint16(0, le) : view(t).getUint32(0, le);
  return v >= lo && v <= hi ? v : undefined;
}

const isLeap = (y: number) => y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);

function date(t: Tag | undefined): string | undefined {
  if (!t || t.type !== 2 || t.count !== 20 || t.raw[19] !== 0) return;
  const s = ascii(t.raw, 0, 19);
  if (!/^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return;
  const [y, mo, d, h, mi, se] = [s.slice(0, 4), s.slice(5, 7), s.slice(8, 10), s.slice(11, 13), s.slice(14, 16), s.slice(17, 19)].map(Number) as [number, number, number, number, number, number];
  const days = [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return y >= 1900 && y <= 2100 && mo >= 1 && mo <= 12 && d >= 1 && d <= days[mo - 1]! && h <= 23 && mi <= 59 && se <= 59 ? s : undefined;
}

function offset(t: Tag | undefined): string | undefined {
  if (!t || t.type !== 2 || t.count !== 7 || t.raw[6] !== 0) return;
  const s = ascii(t.raw, 0, 6);
  if (!/^[+-]\d{2}:\d{2}$/.test(s)) return;
  const mm = Number(s.slice(4, 6));
  const total = (Number(s.slice(1, 3)) * 60 + mm) * (s[0] === "+" ? 1 : -1);
  return mm <= 59 && total >= -720 && total <= 840 ? s : undefined;
}

function subsec(t: Tag | undefined): string | undefined {
  if (!t || t.type !== 2 || t.count < 1) return;
  const nul = t.raw.indexOf(0);
  let end = nul < 0 ? t.raw.length : nul;
  while (end > 0 && t.raw[end - 1] === 0x20) end--; // padding
  if (end < 1 || end > 6) return; // checked before decoding: a long value is not decoded
  const s = ascii(t.raw, 0, end);
  return /^\d{1,6}$/.test(s) ? s : undefined;
}

function lensSpec(t: Tag | undefined, le: boolean): [number, number][] | undefined {
  if (!t || t.type !== 5 || t.count !== 4) return;
  const out: [number, number][] = [];
  for (let i = 0; i < 4; i++) {
    const r = ratAt(view(t), 8 * i, le, false, 0, i < 2 ? 5000 : 64, i >= 2); // 0/0 ("unknown") only in the two f-number slots, not the focal lengths (#384)
    if (!r) return;
    out.push(r);
  }
  return out;
}

// ---- Writing: little-endian, IFD0 then the Exif IFD, values after each directory in entry order ----

interface Entry {
  tag: number;
  type: number;
  count: number;
  value: Uint8Array;
}

const u16 = (v: number) => new Uint8Array([v & 255, (v >> 8) & 255]);
const u32 = (v: number) => new Uint8Array([v & 255, (v >>> 8) & 255, (v >>> 16) & 255, (v >>> 24) & 255]);
const i32 = (v: number) => u32(v >>> 0);
const str = (s: string) => new Uint8Array([...s].map((c) => c.charCodeAt(0)).concat(0));
const rats = (r: [number, number][], signed = false) => cat(r.map(([n, d]) => cat([signed ? i32(n) : u32(n), u32(d)])));
const cat = (parts: Uint8Array[]) => {
  const out = new Uint8Array(parts.reduce((a, p) => a + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
};

function ifdBlob(entries: Entry[], start: number): Uint8Array {
  const sorted = [...entries].sort((a, b) => a.tag - b.tag);
  const parts: Uint8Array[] = [u16(sorted.length)];
  const data: Uint8Array[] = [];
  let dataLen = 0;
  const dataAt = start + 2 + 12 * sorted.length + 4;
  for (const e of sorted) {
    parts.push(u16(e.tag), u16(e.type), u32(e.count));
    if (e.value.length <= 4) parts.push(cat([e.value, new Uint8Array(4 - e.value.length)]));
    else {
      parts.push(u32(dataAt + dataLen));
      const padded = e.value.length % 2 ? cat([e.value, new Uint8Array(1)]) : e.value;
      data.push(padded);
      dataLen += padded.length;
    }
  }
  parts.push(u32(0), ...data);
  return cat(parts);
}

/**
 * The allowed EXIF tags of a block (with or without the JPEG "Exif\0\0" prefix) as a new little-endian TIFF block, or undefined when none is left.
 * `orientation: false` leaves the Orientation tag out, for a picture drawn again with its rotation already applied (#380).
 */
export function allowedExif(block: Uint8Array, { orientation: keepOrientation = true }: { orientation?: boolean } = {}): Uint8Array | undefined {
  const p = parse(block);
  if (!p) return;
  const { le, ifd0, exif } = p;
  const root: Entry[] = [];
  const sub: Entry[] = [];
  const put = (list: Entry[], tag: number, type: number, count: number, value: Uint8Array) => list.push({ tag, type, count, value });
  for (const tag of [0x010f, 0x0110]) {
    const t = ifd0.get(tag);
    const s = t && text(t);
    if (s !== undefined) put(root, tag, 2, s.length + 1, str(s));
  }
  const orientation = keepOrientation ? whole(ifd0.get(0x0112), le, 2, 8) : undefined;
  if (orientation !== undefined) put(root, 0x0112, 3, 1, u16(orientation));
  const rat = (tag: number, lo: number, hi: number, signed = false) => {
    const r = rational(exif.get(tag), le, lo, hi, signed);
    if (r) put(sub, tag, signed ? 10 : 5, 1, rats([r], signed));
  };
  rat(0x829a, 1e-6, 3600);
  // FNumber; when there is none (a manual lens writes only the APEX value), the f-number worked out from ApertureValue:
  // f = 2^(Av / 2) rounded to hundredths, in the same range.
  const fNumber = rational(exif.get(0x829d), le, 0.5, 64);
  const apex = fNumber ? undefined : rational(exif.get(0x9202), le, 0, 12);
  if (fNumber) put(sub, 0x829d, 5, 1, rats([fNumber]));
  else if (apex) {
    const f = Math.pow(2, apex[0] / apex[1] / 2);
    if (f >= 0.5 && f <= 64) {
      const hundredths = Math.floor(f * 100 + 0.5);
      const g = gcd(hundredths, 100);
      put(sub, 0x829d, 5, 1, rats([[hundredths / g, 100 / g]]));
    }
  }
  // ISOSpeedRatings (PhotographicSensitivity); when there is none, ISOSpeed. SensitivityType and StandardOutputSensitivity go.
  const iso = whole(exif.get(0x8827), le, 1, 10_000_000, true) ?? whole(exif.get(0x8833), le, 1, 10_000_000, true);
  if (iso !== undefined) put(sub, 0x8827, iso <= 65535 ? 3 : 4, 1, iso <= 65535 ? u16(iso) : u32(iso));
  const taken = date(exif.get(0x9003));
  if (taken !== undefined) put(sub, 0x9003, 2, 20, str(taken));
  const zone = offset(exif.get(0x9011));
  if (zone !== undefined) put(sub, 0x9011, 2, 7, str(zone));
  rat(0x9204, -10, 10, true);
  const flash = whole(exif.get(0x9209), le, 0, 0x7f);
  if (flash !== undefined) put(sub, 0x9209, 3, 1, u16(flash));
  rat(0x920a, 0.1, 5000);
  const fraction = subsec(exif.get(0x9291));
  if (fraction !== undefined) put(sub, 0x9291, 2, fraction.length + 1, str(fraction));
  const equivalent = whole(exif.get(0xa405), le, 1, 10000);
  if (equivalent !== undefined) put(sub, 0xa405, 3, 1, u16(equivalent));
  const lens = lensSpec(exif.get(0xa432), le);
  if (lens) put(sub, 0xa432, 5, 4, rats(lens));
  for (const tag of [0xa433, 0xa434]) {
    const t = exif.get(tag);
    const s = t && text(t);
    if (s !== undefined) put(sub, tag, 2, s.length + 1, str(s));
  }
  if (!root.length && !sub.length) return;
  if (sub.length) put(root, 0x8769, 4, 1, u32(0));
  const first = ifdBlob(root, 8);
  const rootFinal = sub.length ? root.map((e) => (e.tag === 0x8769 ? { ...e, value: u32(8 + first.length) } : e)) : root;
  const out = cat([new Uint8Array([0x49, 0x49, 0x2a, 0]), u32(8), ifdBlob(rootFinal, 8)]);
  const full = sub.length ? cat([out, ifdBlob(sub, out.length)]) : out;
  return full.length <= MAX_BLOCK ? full : undefined;
}

/** The EXIF orientation (1 to 8) in a block with or without the "Exif\0\0" prefix, if it has one. Not checked beyond its range. */
export function exifOrientationOf(block: Uint8Array): number | undefined {
  const p = parse(block);
  if (!p) return;
  const o = whole(p.ifd0.get(0x0112), p.le, 1, 8);
  return o;
}

/**
 * The photo record (#379) of an allowlist block: what the viewer's info panel shows, with every value read from the checked block
 * `allowedExif` wrote, never from the source. Undefined when the block holds no camera, lens or shot setting: the time alone is
 * not camera info. The camera is the model; a model that starts with the make's first word ("LEICA M11-P" with a make of "Leica
 * Camera AG") is written with the make's spelling ("Leica M11-P"); a camera with no model is its make.
 */
export function photoOf(block: Uint8Array): PhotoInfo | undefined {
  const p = parse(block);
  if (!p) return;
  const { le, ifd0, exif } = p;
  const info: PhotoInfo = {};
  const make = ifd0.get(0x010f) && text(ifd0.get(0x010f)!);
  const model = ifd0.get(0x0110) && text(ifd0.get(0x0110)!);
  if (model) {
    const first = make?.split(" ")[0];
    info.camera = first && model.toLowerCase().startsWith(`${first.toLowerCase()} `) ? `${first}${model.slice(first.length)}` : model;
  } else if (make) info.camera = make;
  const lens = exif.get(0xa434) && text(exif.get(0xa434)!);
  if (lens) info.lens = lens;
  const num = (tag: number, lo: number, hi: number) => {
    const r = rational(exif.get(tag), le, lo, hi);
    return r ? r[0] / r[1] : undefined;
  };
  const fNumber = num(0x829d, 0.5, 64);
  if (fNumber !== undefined) info.fNumber = fNumber;
  const exposure = num(0x829a, 1e-6, 3600);
  if (exposure !== undefined) info.exposure = exposure;
  const iso = whole(exif.get(0x8827), le, 1, 10_000_000, true);
  if (iso !== undefined) info.iso = iso;
  const focal = num(0x920a, 0.1, 5000);
  if (focal !== undefined) info.focal = focal;
  const focal35 = whole(exif.get(0xa405), le, 1, 10000);
  if (focal35 !== undefined) info.focal35 = focal35;
  if (Object.keys(info).length === 0) return;
  const taken = date(exif.get(0x9003));
  if (taken !== undefined) {
    info.takenAt = `${taken.slice(0, 4)}-${taken.slice(5, 7)}-${taken.slice(8, 10)}T${taken.slice(11)}`;
    const zone = offset(exif.get(0x9011));
    if (zone !== undefined) info.offset = zone;
  }
  return info;
}
